import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/partner.css";
import { PageLoader } from "../components/page-loader";

const UserDomainsAdmin = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingDomains, setDeletingDomains] = useState({});
  const [removingPartners, setRemovingPartners] = useState({});
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(null);
  const isDeletingRef = useRef(false);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [showSharePopup, setShowSharePopup] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [shareError, setShareError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const fetchDomains = useCallback(async () => {
    try {
      const response = await fetch(
        `https://sst.fjellhosting.no/user-domains-admin`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch domains");
      }

      const { domains } = await response.json();
      setDomains(domains);
      setLoading(false);
    } catch (error) {
      setError(`Error: ${error.message}`);
      setLoading(false);
      console.error("Error:", error);
    }
  }, [accessToken, userEmail]);

  useEffect(() => {
    if (accessToken && userEmail) {
      fetchDomains();
    }
  }, [accessToken, userEmail, fetchDomains]);

  const getMaxRequestCount = (request_size) => {
    switch (request_size) {
      case "packone":
        return "10 000";
      case "packtwo":
        return "500 000";
      case "packthree":
        return "1 000 000";
      case "packfour":
        return "5 000 000";
      case "packfive":
        return "10 000 000";
      default:
        return "Unknown";
    }
  };

  const toggleSettingsMenu = (domain) => {
    setShowSettingsMenu((prev) => ({
      ...prev,
      [domain]: !prev[domain],
    }));
  };

  const showDeleteConfirmationPopup = (domain) => {
    setShowDeleteConfirmation(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
  };

  const deleteDomain = async (domain) => {
    if (isDeletingRef.current || !accessToken || !userEmail) return;
    isDeletingRef.current = true;

    try {
      setDeletingDomains((prev) => ({ ...prev, [domain]: true }));

      const response = await fetch(`https://sst.fjellhosting.no/run-script2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({ domain }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete domain");
      }

      const { jobId } = await response.json();
      await pollJobStatus(jobId, domain);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
    } finally {
      isDeletingRef.current = false;
    }
  };

  const pollJobStatus = async (jobId, domain) => {
    try {
      const interval = setInterval(async () => {
        const response = await fetch(
          `https://sst.fjellhosting.no/job-status/${jobId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-User-Email": userEmail,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to check job status");
        }

        const { status, error } = await response.json();

        if (status === "completed") {
          setDomains((prev) =>
            prev.map((d) => (d.domain === domain ? { ...d, deleted: true } : d))
          );
          clearInterval(interval);
          setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
          setShowDeleteConfirmation(null);
          window.location.reload();
        } else if (status === "failed") {
          throw new Error(error || "Failed to delete domain");
        }
      }, 2000);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
      setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const showSharePopupHandler = (domain) => {
    setShowSharePopup(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
    setShareEmail("");
    setShareError("");
  };

  const handleShareEmailChange = (e) => {
    setShareEmail(e.target.value);
    setShareError("");
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const sanitizeEmail = (email) => {
    return email.trim().toLowerCase();
  };

  const shareDomain = async (domain) => {
    const sanitizedEmail = sanitizeEmail(shareEmail);

    if (!validateEmail(sanitizedEmail)) {
      setShareError("Please enter a valid email address");
      return;
    }

    try {
      const response = await fetch(`https://sst.fjellhosting.no/partner-add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({
          domain,
          partnerEmail: sanitizedEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to share domain");
      }

      setShareEmail("");
      setShareError("");

      setShareError({
        type: "success",
        message: `Delt ${domain} med ${sanitizedEmail}. Last inn siden på nytt for å se endringer.`,
      });
    } catch (error) {
      setShareError({
        type: "error",
        message: error.message,
      });
      console.error("Error sharing domain:", error);
    }
  };

  const removePartner = async (domain, partnerEmail) => {
    try {
      setRemovingPartners((prev) => ({
        ...prev,
        [`${domain}-${partnerEmail}`]: true,
      }));

      const response = await fetch(
        `https://sst.fjellhosting.no/partner-remove`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
          body: JSON.stringify({
            domain,
            partnerEmail,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to remove partner");
      }

      setDomains((prevDomains) =>
        prevDomains.map((d) =>
          d.domain === domain
            ? {
                ...d,
                partner_emails: d.partner_emails.filter(
                  (e) => e !== partnerEmail
                ),
              }
            : d
        )
      );

      setShareError({
        type: "success",
        message: `Successfully removed ${partnerEmail} from ${domain}`,
      });
    } catch (error) {
      setShareError({
        type: "error",
        message: error.message,
      });
      console.error("Error removing partner:", error);
    } finally {
      setRemovingPartners((prev) => ({
        ...prev,
        [`${domain}-${partnerEmail}`]: false,
      }));
    }
  };

  return (
    <div className="user-domains">
      <h2>Dine servere: Gammel server.</h2>
      {loading ? (
        <PageLoader />
      ) : error ? (
        <p className="error">{error}</p>
      ) : domains.length === 0 ? (
        <p>Du har ingen domener.</p>
      ) : (
        <ul>
          {domains.map(
            (
              { domain, status, requestCount, request_size, partner_emails },
              index
            ) => (
              <li key={index}>
                {domain}
                <span className={`status ${status}`}>{status}</span>
                <span className="request-count">
                  Forespørsler:{" "}
                  {requestCount !== undefined && request_size !== undefined
                    ? `${requestCount}/${getMaxRequestCount(request_size)}`
                    : "Loading..."}
                </span>
                <div className="settings-menu">
                  <button
                    className="settings-button"
                    onClick={() => toggleSettingsMenu(domain)}
                  >
                    &#8942;
                  </button>
                  {showSettingsMenu[domain] && (
                    <div className="settings-dropdown">
                      <button
                        onClick={() => showDeleteConfirmationPopup(domain)}
                        disabled={deletingDomains[domain]}
                      >
                        Slett
                      </button>
                      <button
                        onClick={() => showSharePopupHandler(domain)}
                        disabled={deletingDomains[domain]}
                      >
                        Del
                      </button>
                    </div>
                  )}
                </div>
                {showDeleteConfirmation === domain && (
                  <div className="delete-confirmation-popup">
                    <p>Er du sikker på at du vil slette {domain}?</p>
                    <button
                      onClick={() => deleteDomain(domain)}
                      disabled={deletingDomains[domain]}
                    >
                      {deletingDomains[domain] ? "Sletter..." : "Ja"}
                    </button>
                    <button onClick={() => setShowDeleteConfirmation(null)}>
                      Nei
                    </button>
                  </div>
                )}
                {showSharePopup === domain && (
                  <div className="share-popup">
                    <h3>Del {domain}</h3>
                    <p>Skriv inn e-postadresse:</p>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={handleShareEmailChange}
                      placeholder="partner@example.com"
                    />
                    {shareError && (
                      <div className={`message ${shareError.type || "error"}`}>
                        {shareError.message}
                        <button
                          onClick={() => setShareError(null)}
                          className="message-close"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <div className="popup-buttons">
                      <button
                        onClick={() => shareDomain(domain)}
                        disabled={!shareEmail || shareError?.type === "success"}
                      >
                        {shareError?.type === "success"
                          ? "Sendt!"
                          : "Send invitasjon"}
                      </button>
                      <button
                        onClick={() => {
                          setShowSharePopup(null);
                          setShareError(null);
                        }}
                      >
                        Avbryt
                      </button>
                    </div>
                    {partner_emails && partner_emails.length > 0 && (
                      <div className="partner-list">
                        <h4>Delt med:</h4>
                        <ul>
                          {partner_emails.map((email) => (
                            <li key={email}>
                              {email}
                              <button
                                onClick={() => removePartner(domain, email)}
                                disabled={
                                  removingPartners[`${domain}-${email}`]
                                }
                                className="remove-partner-btn"
                              >
                                {removingPartners[`${domain}-${email}`]
                                  ? "Fjerner..."
                                  : "Fjern"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default UserDomainsAdmin;
